import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

type TourState = {
  isOpen: boolean;
  steps: TourStep[];
  index: number;
};

const DEFAULT_WAIT_MS = 1500;

@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly state$ = new BehaviorSubject<TourState>({
    isOpen: false,
    steps: [],
    index: 0,
  });

  readonly vm$ = this.state$.asObservable();

  get snapshot(): TourState {
    return this.state$.value;
  }

  start(steps: TourStep[], startIndex = 0) {
    this.state$.next({ isOpen: true, steps, index: startIndex });
  }

  close() {
    this.state$.next({ ...this.snapshot, isOpen: false });
  }

  async next() {
    const s = this.snapshot;
    if (!s.isOpen) return;

    const cur = s.steps[s.index];
    if (cur?.afterHide) await cur.afterHide();

    const nextIndex = Math.min(s.index + 1, s.steps.length - 1);
    this.state$.next({ ...s, index: nextIndex });
  }

  async prev() {
    const s = this.snapshot;
    if (!s.isOpen) return;

    const cur = s.steps[s.index];
    if (cur?.afterHide) await cur.afterHide();

    const prevIndex = Math.max(s.index - 1, 0);
    this.state$.next({ ...s, index: prevIndex });
  }

  // Used by overlay to ensure target exists before showing
  async waitForTarget(selector: string, waitMs?: number): Promise<HTMLElement | null> {
    const timeout = waitMs ?? DEFAULT_WAIT_MS;
    const start = performance.now();

    while (performance.now() - start < timeout) {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el) return el;
      await new Promise(r => requestAnimationFrame(() => r(null)));
    }
    return null;
  }
}


export type TourPlacement = 'auto' | 'top' | 'right' | 'bottom' | 'left';

export type TourStep = {
  id: string;
  target: string; // CSS selector, e.g. '#tour-pattern'
  title: string;
  body: string;
  placement?: TourPlacement;

  // Optional lifecycle hooks
  beforeShow?: () => void | Promise<void>;
  afterHide?: () => void | Promise<void>;

  // If target might be inside *ngIf, allow waiting for it
  waitForTargetMs?: number;

  // Optional: adjust highlight padding
  padding?: number;
};
